import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import { reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import ReactMarkdown from 'markdown-to-jsx';

import Field from '@ynap/field';
import Overlay from '@ynap/overlay';
import { Button } from '@ynap/button';
import Checkbox from '@ynap/checkbox';
import BEMHelper from '@ynap/bem-helper';
import DateSelect from '@ynap/date-select';
import { PasswordInput } from '@ynap/password-input';
import { CombinedSelectWithCustomValue } from '@ynap/select';
import { RadioButtonGroup, RadioButton } from '@ynap/radio-button';
import { authActions, getAutomaticMarketingConsent } from '@ynap/auth-state';
import { onSubmitFail, submitAndCollateErrorsPromise } from '@ynap/form-utils';

import { trackMarketingConsent, trackReadPrivacyPolicy, trackReadAboutEmail } from '../analytics';

import TcMessage from './TcMessage';
import TcOverlay from './TcOverlay';
import validate from './validate';
import collateErrors from './collateErrors';

const bem = new BEMHelper('RegistrationForm');

@track()
export class RegistrationForm extends PureComponent {
    static propTypes = {
        form: PropTypes.string,
        submitting: PropTypes.bool,
        handleSubmit: PropTypes.func,
        onSubmit: PropTypes.func,
        children: PropTypes.node,
        titles: PropTypes.array.isRequired,
        className: PropTypes.string,
        style: PropTypes.object,
        links: PropTypes.shape({
            termsAndConditions: PropTypes.string,
            privacyPolicy: PropTypes.string,
        }),
        hideLogonId: PropTypes.bool,
        enableTitles: PropTypes.bool,
        component: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        messages: PropTypes.shape({
            registrationForm: PropTypes.shape({
                register: PropTypes.func.isRequired,
                logonId: PropTypes.func.isRequired,
                logonPassword: PropTypes.func.isRequired,
                personTitle: PropTypes.func.isRequired,
                firstName: PropTypes.func.isRequired,
                lastName: PropTypes.func.isRequired,
                phone1: PropTypes.func,
                dateOfBirth: PropTypes.func,
                day: PropTypes.func,
                month: PropTypes.func,
                year: PropTypes.func,
                receiveEmailPreference: PropTypes.func.isRequired,
                maMarketingConsent: PropTypes.func,
                selectTitle: PropTypes.func.isRequired,
                other: PropTypes.func.isRequired,
                checkAll: PropTypes.func,
                ageConsent: PropTypes.func,
                tcConsent: PropTypes.func,
                newsletterConsent: PropTypes.func,
            }).isRequired,
            months: PropTypes.shape({
                january: PropTypes.func,
                february: PropTypes.func,
                march: PropTypes.func,
                april: PropTypes.func,
                may: PropTypes.func,
                june: PropTypes.func,
                july: PropTypes.func,
                august: PropTypes.func,
                september: PropTypes.func,
                october: PropTypes.func,
                november: PropTypes.func,
                december: PropTypes.func,
            }),
            titles: PropTypes.objectOf(PropTypes.func).isRequired,
            errors: PropTypes.shape({
                maxLength: PropTypes.func.isRequired,
                minLength: PropTypes.func.isRequired,
                mustContainNumber: PropTypes.func.isRequired,
                mustContainLetter: PropTypes.func.isRequired,
                containsWhitespace: PropTypes.func.isRequired,
                characterOccurrence: PropTypes.func.isRequired,
                containsLogonId: PropTypes.func.isRequired,
                containsPassword: PropTypes.func.isRequired,
                passwordsDontMatch: PropTypes.func.isRequired,
                invalid: PropTypes.func.isRequired,
                required: PropTypes.func.isRequired,
                generic: PropTypes.func.isRequired,
                fieldErrors: PropTypes.func.isRequired,
                global: PropTypes.func.isRequired,
                userExists: PropTypes.func.isRequired,
            }).isRequired,
            tcOverlay: PropTypes.object,
            newsletterOverlay: PropTypes.object,
        }).isRequired,
        handleToggleMktConsent: PropTypes.func,
        supportedLanguages: PropTypes.arrayOf(PropTypes.string),
        maMarketingConsent: PropTypes.string,
        preferredLanguage: PropTypes.string,
        locale: PropTypes.shape({
            country: PropTypes.string,
            currency: PropTypes.string,
            id: PropTypes.string,
            language: PropTypes.string,
        }),
        gdprConsent: PropTypes.bool,
        enableGenderSelection: PropTypes.bool,
        enableAgeConsent: PropTypes.bool,
        enableTcConsent: PropTypes.bool,
        field: PropTypes.string,
    };

    constructor(props) {
        super(props);

        this.state = {
            showOverlay: false,
            showTcOverlay: false,
            showNewsletterOverlay: false,
        };

        this.handleOverlayBodyClick = this.handleOverlayBodyClick.bind(this);
        this.handleEmailOverlay = this.handleEmailOverlay.bind(this);
        this.handleTcOverlayBodyClick = this.handleTcOverlayBodyClick.bind(this);
        this.handleTcOverlay = this.handleTcOverlay.bind(this);
        this.handleNewsletterOverlayBodyClick = this.handleNewsletterOverlayBodyClick.bind(this);
        this.handleNewsletterOverlay = this.handleNewsletterOverlay.bind(this);
    }

    static defaultProps = { component: 'form' };

    handleOverlayBodyClick() {
        this.setState({ showOverlay: false });
    }

    handleNewsletterOverlayBodyClick() {
        this.setState({ showNewsletterOverlay: false });
    }

    handleTcOverlayBodyClick() {
        this.setState({ showTcOverlay: false });
    }

    handleEmailOverlay() {
        this.setState({ showOverlay: true });
    }

    handleNewsletterOverlay() {
        this.setState({ showNewsletterOverlay: true });
    }

    handleTcOverlay() {
        this.setState({ showTcOverlay: true });
    }

    submit = (values, delay) => {
        const { onSubmit } = this.props;
        return submitAndCollateErrorsPromise(onSubmit(values, undefined, delay), values, this.props);
    };

    render() {
        const {
            form,
            component: Component,
            submitting,
            handleSubmit,
            messages,
            className,
            children,
            hideLogonId,
            enableTitles,
            links,
            handleToggleMktConsent,
            supportedLanguages,
            maMarketingConsent,
            preferredLanguage,
            locale,
            enableGenderSelection,
            enableAgeConsent,
            enableTcConsent,
            gdprConsent,
            changeField,
            field,
        } = this.props;

        const months = Object.keys(messages.months).map((month) => messages.months[month]());
        const titleOptions = Object.keys(messages.titles).reduce((acc, title) => {
            const titleName = messages.titles[title]();
            if (titleName === '') return acc;

            if (title === 'other') {
                acc.push({
                    key: '_custom',
                    value: '_custom',
                    label: titleName,
                    labelNative: titleName,
                });
            } else {
                acc.push({
                    key: titleName,
                    value: titleName,
                    label: titleName,
                    labelNative: titleName,
                });
            }
            return acc;
        }, []);

        return (
            <Component id={form} className={bem(null, { submitting }, className)} onSubmit={handleSubmit ? handleSubmit(this.submit) : undefined}>
                {!hideLogonId ? <Field name="logonId" type="email" label={messages.registrationForm.logonId()} /> : null}
                {children}
                <Field
                    component={PasswordInput}
                    name="logonPassword"
                    label={messages.registrationForm.logonPassword()}
                    autoComplete="password"
                    help={messages.help.passwordRules()}
                />
                {enableTitles ? (
                    <Field
                        component={CombinedSelectWithCustomValue}
                        className={bem('title')}
                        name="personTitle"
                        label={messages.registrationForm.personTitle()}
                        emptyLabel={messages.registrationForm.selectTitle()}
                        autoComplete="honorific-prefix"
                        options={[...titleOptions]}
                    />
                ) : null}
                <Field name="firstName" type="text" label={messages.registrationForm.firstName()} autoComplete="given-name" />
                <Field name="lastName" type="text" label={messages.registrationForm.lastName()} autoComplete="family-name" />
                {messages.registrationForm.phone1() ? <Field name="phone1" label={messages.registrationForm.phone1()} autoComplete="tel" /> : null}
                {messages.registrationForm.dateOfBirth() && locale.country === 'KR' ? (
                    <Field
                        component={DateSelect}
                        name="dateOfBirth"
                        label={messages.registrationForm.dateOfBirth()}
                        months={months}
                        firstYear={1901}
                        dayLabel={messages.registrationForm.day()}
                        monthLabel={messages.registrationForm.month()}
                        yearLabel={messages.registrationForm.year()}
                    />
                ) : null}
                {messages.registrationForm.maMarketingConsent() ? (
                    <Consent
                        form={form}
                        parentForm="registration"
                        messages={messages}
                        links={links}
                        handleEmailOverlay={this.handleEmailOverlay}
                        maMarketingConsent={maMarketingConsent}
                        handleToggleMktConsent={handleToggleMktConsent}
                        supportedLanguages={supportedLanguages}
                        preferredLanguage={preferredLanguage}
                        locale={locale}
                        enableGenderSelection={enableGenderSelection}
                        gdprConsent={gdprConsent}
                        handleNewsletterOverlay={this.handleNewsletterOverlay}
                        enableTcConsent={enableTcConsent}
                        enableAgeConsent={enableAgeConsent}
                        changeField={changeField}
                        handleTcOverlay={this.handleTcOverlay}
                        field={field}
                    />
                ) : null}

                {handleSubmit ? (
                    <Button type="submit" className={bem('submit')} disabled={submitting}>
                        {messages.registrationForm.register()}
                    </Button>
                ) : null}

                {this.state.showOverlay && emailInfoOverlay(messages, locale, this.handleOverlayBodyClick)}
                {this.state.showTcOverlay && <TcOverlay messages={messages} handleClose={this.handleTcOverlayBodyClick} locale={locale} />}
                {this.state.showNewsletterOverlay && (
                    <TcOverlay messages={messages} handleClose={this.handleNewsletterOverlayBodyClick} locale={locale} isNewsletterTc />
                )}
            </Component>
        );
    }
}

const { selectMarketingConsentIfNeeded } = authActions;
@track()
@connect(getAutomaticMarketingConsent, { selectMarketingConsentIfNeeded })
export class Consent extends PureComponent {
    static propTypes = {
        parentForm: PropTypes.string.isRequired,
        locale: PropTypes.shape({
            language: PropTypes.string.isRequired,
            id: PropTypes.string.isRequired,
            country: PropTypes.string.isRequired,
        }).isRequired,
        className: PropTypes.string,
        supportedLanguages: PropTypes.arrayOf(PropTypes.string),
        maMarketingConsent: PropTypes.string,
        handleToggleMktConsent: PropTypes.func,
        handleEmailOverlay: PropTypes.func,
        selectMarketingConsentIfNeeded: PropTypes.func,
        handleNewsletterOverlay: PropTypes.func,
        messages: {
            registrationForm: PropTypes.shape({
                maMarketingConsent: PropTypes.func.isRequired,
                emailReadMore: PropTypes.func.isRequired,
            }).isRequired,
        },
        changeField: PropTypes.func,
        handleTcOverlay: PropTypes.func,
        enableAgeConsent: PropTypes.bool,
        enableTcConsent: PropTypes.bool,
        gdprConsent: PropTypes.boolean,
        ageConsentField: PropTypes.boolean,
        tcConsentField: PropTypes.boolean,
        field: PropTypes.string,
        enableGenderSelection: PropTypes.boolean,
        automaticMarketingConsent: PropTypes.boolean,
        showTermsAndConditions: PropTypes.boolean,
        preferredLanguage: PropTypes.string,
        links: PropTypes.shape({
            privacyPolicy: PropTypes.string,
        }),
        optimizely: PropTypes.object,
    };

    constructor() {
        super();
        this.state = {
            ageConsent: false,
            tcConsnet: false,
            checkAll: false,
        };

        this.handleEmailClick = this.handleEmailClick.bind(this);
        this.handlePrivacyPolicyClick = this.handlePrivacyPolicyClick.bind(this);
        this.handleRadioBtnClick = this.handleRadioBtnClick.bind(this);
        this.handleChangeMktLang = this.handleChangeMktLang.bind(this);
        this.handleCheckboxClick = this.handleCheckboxClick.bind(this);

        this.state = {
            selectedLanguage: null,
        };
    }

    componentDidMount() {
        const { form, selectMarketingConsentIfNeeded, field } = this.props;
        selectMarketingConsentIfNeeded && selectMarketingConsentIfNeeded(form, field);
    }

    @track(trackReadAboutEmail)
    handleEmailClick() {
        this.props.handleEmailOverlay?.();
    }

    @track(trackReadPrivacyPolicy)
    handlePrivacyPolicyClick() { }

    @track(({ checkoutAnalytics }, state, [event]) => {
        const [consentValue] = event.currentTarget.value.split('-');
        return trackMarketingConsent(consentValue === 'true', checkoutAnalytics);
    })
    handleRadioBtnClick(e) {
        const [consentValue, langISO] = e.currentTarget.value.split('-');
        const {
            locale: { language },
            supportedLanguages,
        } = this.props;
        const preferredLanguage = langISO || supportedLanguages?.filter((locale) => locale.includes(language))[0];
        this.props.handleToggleMktConsent?.(consentValue, preferredLanguage);
    }

    @track(({ checkoutAnalytics }, _, [e]) => trackMarketingConsent(e.target.checked, checkoutAnalytics))
    handleCheckboxClick(e, currentISO2Lang = null) {
        const { id: fieldNameWithForm, checked } = e.currentTarget;
        const dividedFieldName = fieldNameWithForm.split('.');
        const id = dividedFieldName[dividedFieldName.length - 1];
        const { maMarketingConsent, handleToggleMktConsent, parentForm, enableAgeConsent, enableTcConsent } = this.props;
        const multipleConsents = enableAgeConsent || enableTcConsent;
        const { ageConsent, tcConsent } = this.state;
        const ageConsentCurrent = id === 'ageConsent' ? checked : ageConsent;
        const tcConsentCurrent = id === 'tcConsent' ? checked : tcConsent;
        const maMarketingConsentCurrent = id === 'maMarketingConsent' ? checked : maMarketingConsent === 'true';
        const currentCheckedAll = ageConsentCurrent && tcConsentCurrent && maMarketingConsentCurrent;
        const formPrefix = parentForm ? `${parentForm}.` : '';

        this.changeField(`${formPrefix}${dividedFieldName}`, id, id === 'maMarketingConsent' ? checked.toString() : checked);

        if (multipleConsents && ['ageConsent', 'tcConsent', 'maMarketingConsent'].includes(id)) {
            this.changeField(`${formPrefix}checkAll`, 'checkAll', currentCheckedAll);
        }

        if (id === 'checkAll') {
            const checkedToggled = !currentCheckedAll;
            this.setState({ ageConsent: checkedToggled, tcConsent: checkedToggled });
            handleToggleMktConsent(checkedToggled.toString(), currentISO2Lang);
            this.changeField(`${formPrefix}ageConsent`, 'ageConsent', checkedToggled);
            this.changeField(`${formPrefix}tcConsent`, 'tcConsent', checkedToggled);
            this.changeField(`${formPrefix}maMarketingConsent`, 'maMarketingConsent', checkedToggled.toString());
        }

        if (id === 'maMarketingConsent') {
            handleToggleMktConsent(checked.toString(), currentISO2Lang);
            this.changeField(`${formPrefix}maMarketingConsent`, 'maMarketingConsent', checked.toString());
            // FECC-424 TON Post Checkout Customer Registration A/B test
            this.props.optimizely?.track('oc_registration_news_opt_in');
        }
    }

    changeField(fieldNameWithForm, id, checked) {
        const { changeField: changeReduxField } = this.props;
        this.setState({ [id]: checked });
        changeReduxField(fieldNameWithForm, checked);
    }

    handleChangeMktLang(e) {
        const langISO = e.currentTarget.value;
        this.props.handleToggleMktConsent?.(null, langISO);
    }

    render() {
        const {
            messages,
            className = '',
            maMarketingConsent,
            handleNewsletterOverlay,
            enableTcConsent,
            enableAgeConsent,
            gdprConsent,
            links,
            locale: { language: defaultISO2Language, id: localeId, country },
            supportedLanguages,
            enableGenderSelection,
            automaticMarketingConsent,
            showTermsAndConditions = true,
            preferredLanguage,
            handleTcOverlay,
        } = this.props;
        const multipleConsents = enableAgeConsent || enableTcConsent;
        const { checkAll, ageConsent, tcConsent } = this.state;
        const preferredLanguageISO2 = preferredLanguage?.length > 2 ? preferredLanguage.split('_')[0] : preferredLanguage;
        const currentISO2Lang = preferredLanguageISO2 || defaultISO2Language;

        return (
            <div className={bem('consent')}>
                {multipleConsents && (
                    <Field
                        className={bem('checkbox')}
                        name="checkAll"
                        component={Checkbox}
                        checked={checkAll}
                        onChange={(e) => this.handleCheckboxClick(e, currentISO2Lang)}
                        onBlur={(e) => e.preventDefault()}
                    >
                        {messages.registrationForm.checkAll()}
                    </Field>
                )}
                {enableAgeConsent && (
                    <Field
                        className={bem('checkbox')}
                        name="ageConsent"
                        component={Checkbox}
                        checked={ageConsent}
                        onChange={this.handleCheckboxClick}
                        onBlur={(e) => e.preventDefault()}
                    >
                        {messages.registrationForm.ageConsent()}
                    </Field>
                )}
                {enableTcConsent && (
                    <Field
                        className={bem('checkbox')}
                        name="tcConsent"
                        component={Checkbox}
                        checked={tcConsent}
                        onChange={this.handleCheckboxClick}
                        onBlur={(e) => e.preventDefault()}
                    >
                        <ReactMarkdown
                            options={{
                                overrides: {
                                    a: {
                                        component: Button,
                                        props: {
                                            className: bem('link'),
                                            type: 'link',
                                            component: 'a',
                                            target: '_blank',
                                            rel: 'noopener',
                                            onClick: (e) => {
                                                e.preventDefault();
                                                handleTcOverlay();
                                            }
                                        },
                                    },
                                },
                            }}
                        >
                            {messages.registrationForm.tcConsent()}
                        </ReactMarkdown>
                    </Field>
                )}
                <div className={className}>
                    {multipleConsents ? (
                        <>
                            <Field
                                className={bem('checkbox')}
                                name="maMarketingConsent"
                                checked={maMarketingConsent === 'true'}
                                format={(value) => value === 'true' && maMarketingConsent === 'true'}
                                normalize={(value) => value.toString()}
                                component={Checkbox}
                                onBlur={(e) => e.preventDefault()}
                                onChange={this.handleCheckboxClick}
                            >
                                <ReactMarkdown
                                    options={{
                                        overrides: {
                                            a: {
                                                component: Button,
                                                props: {
                                                    className: bem('link'),
                                                    type: 'link',
                                                    component: 'a',
                                                    target: '_blank',
                                                    rel: 'noopener',
                                                    onClick: (e) => {
                                                        e.preventDefault();
                                                        handleNewsletterOverlay();
                                                    }
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {messages.registrationForm.newsletterConsent()}
                                </ReactMarkdown>
                            </Field>

                            <Field
                                className={bem('marketing', 'withCheckbox')}
                                name="marketingLanguage"
                                component={RadioButtonGroup}
                                onChange={this.handleChangeMktLang}
                                onBlur={(e) => e.preventDefault()}
                            >
                                {maMarketingConsent === 'true' &&
                                    supportedLanguages?.length > 1 &&
                                    supportedLanguages.map((langISO) => {
                                        const twoCharacterCountryISO = langISO.split('_')[0];
                                        const shouldBeChecked = maMarketingConsent === 'true' && twoCharacterCountryISO === currentISO2Lang;
                                        const shouldBeCheckedModifier = shouldBeChecked ? 'isChecked' : null;
                                        const langISOWithCountry = langISO.length === 2 ? `${langISO}_${country.toUpperCase()}` : langISO;

                                        return (
                                            messages?.languages?.[twoCharacterCountryISO] && (
                                                <RadioButton
                                                    key={langISOWithCountry}
                                                    value={langISOWithCountry}
                                                    label={messages.languages[twoCharacterCountryISO]()}
                                                    name={langISOWithCountry}
                                                    showError={true}
                                                    className={bem('maPreferredLang', ['withCheckbox', shouldBeCheckedModifier])}
                                                />
                                            )
                                        );
                                    })}
                            </Field>
                        </>
                    ) : (
                        messages.registrationForm.maMarketingConsent && (
                            <div className={className}>
                                <Field
                                    className={bem('marketing')}
                                    name="maMarketingConsent"
                                    checked={maMarketingConsent === 'true'}
                                    component={Checkbox}
                                    format={(value) => value === 'true'}
                                    normalize={(value) => value?.toString()}
                                    onChange={(e) => this.handleCheckboxClick(e, currentISO2Lang)}
                                    onBlur={(e) => e.preventDefault()}
                                >
                                    {enableTcConsent ? (
                                        <ReactMarkdown
                                            options={{
                                                overrides: {
                                                    a: {
                                                        component: Button,
                                                        props: {
                                                            className: bem('link'),
                                                            type: 'link',
                                                            component: 'a',
                                                            target: '_blank',
                                                            rel: 'noopener',
                                                            onClick: (e) => {
                                                                e.preventDefault();
                                                                handleNewsletterOverlay();
                                                            }
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            {messages.registrationForm.newsletterConsent()}
                                        </ReactMarkdown>
                                    ) : (
                                        <label className={bem('marketingConsentText')} htmlFor="maMarketingConsent">
                                            {messages.registrationForm.maMarketingConsent()}
                                        </label>
                                    )}
                                </Field>
                                {!enableTcConsent && (
                                    <Button
                                        type="link"
                                        component="a"
                                        target="_blank"
                                        rel="noopener"
                                        onClick={this.handleEmailClick}
                                        className={bem('emailLink')}
                                    >
                                        {messages.registrationForm.emailReadMore()}
                                    </Button>
                                )}
                            </div>
                        )
                    )}
                    {maMarketingConsent === 'true' && enableGenderSelection && (
                        <Field
                            name="gender"
                            component={RadioButtonGroup}
                            onBlur={(e) => e.preventDefault()}
                            label={messages.registrationForm.genderSelectionLabel()}
                            className={bem('gender', multipleConsents && 'withCheckbox')}
                        >
                            <RadioButton value="Female" label={messages.registrationForm.genderWomens()} showError={true} className={bem('genderOption')} />
                            <RadioButton value="Male" label={messages.registrationForm.genderMens()} showError={true} className={bem('genderOption')} />
                            <RadioButton value="Unspecified" label={messages.registrationForm.genderAll()} showError={true} className={bem('genderOption')} />
                        </Field>
                    )}

                    {gdprConsent ? (
                        <div className={bem('gdprConsentContainer')}>
                            <Field
                                className={bem('gdprConsent')}
                                name="gdprConsent"
                                component={Checkbox}
                                format={(value) => value === 'true'}
                                normalize={(value) => value.toString()}
                                onBlur={(e) => e.preventDefault()}
                            >
                                <label className={bem('marketingConsentText', 'gdprConsent')} htmlFor="gdprConsent">
                                    <ReactMarkdown
                                        options={{
                                            overrides: {
                                                a: {
                                                    component: Button,
                                                    props: {
                                                        className: bem('link'),
                                                        type: 'link',
                                                        component: 'a',
                                                        target: '_blank',
                                                        rel: 'noopener',
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        {messages.registrationForm.gdprConsentTerms({ locale: localeId })}
                                    </ReactMarkdown>{' '}
                                    <ReactMarkdown
                                        options={{
                                            overrides: {
                                                a: {
                                                    component: Button,
                                                    props: {
                                                        className: bem('link'),
                                                        type: 'link',
                                                        component: 'a',
                                                        target: '_blank',
                                                        rel: 'noopener',
                                                        onClick: this.handlePrivacyPolicyClick
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        {messages.registrationForm.gdprConsentPrivacy({ locale: localeId })}
                                    </ReactMarkdown>
                                </label>
                            </Field>
                        </div>
                    ) : (
                        showTermsAndConditions && (
                            <TcMessage messages={messages} links={links} onPrivacyClick={this.handlePrivacyPolicyClick} localeId={localeId} />
                        )
                    )}
                </div>
            </div>
        );
    }
}

export const emailInfoOverlay = (messages, locale, handleClose) => (
    <Overlay className={bem('emailModal')} title={messages.registrationForm.emailTitle()} onClose={handleClose} handleBodyClick={handleClose}>
        <ReactMarkdown
            options={{
                wrapper: React.Fragment,
                overrides: {
                    a: {
                        component: Button,
                        props: {
                            className: bem('link'),
                            type: 'link',
                            component: 'a',
                            target: '_blank',
                            rel: 'noopener'
                        },
                    },
                },
            }}
        >
            {messages.registrationForm.emailInfo({ locale: locale.id })}
        </ReactMarkdown>
    </Overlay>
);

const RegistrationFormContainer = connect((state) => {
    return {
        enableTitles: state.config ? state.config.head.flags.enableTitles : true,
    };
})(RegistrationForm);

export default reduxForm({ form: 'registration', validate, collateErrors, onSubmitFail })(RegistrationFormContainer);
